import { useState, type FormEvent, type ReactNode } from 'react';
import { ApiError, guestApi } from '../lib/api.js';
import { Icon } from '../lib/icons.js';
import { Ambient } from '../components/ui.js';

/** Formats as the guest types: 8 characters, a dash after the fourth. */
function format(raw: string): string {
  const clean = raw.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8);
  return clean.length > 4 ? `${clean.slice(0, 4)}-${clean.slice(4)}` : clean;
}

export function CodeEntry({ onSuccess }: { onSuccess: () => void }): ReactNode {
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [shake, setShake] = useState(0);

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (busy || code.replace(/-/g, '').length < 4) return;
    setBusy(true);
    setError(null);
    try {
      await guestApi.redeem(code);
      onSuccess();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not reach the house.');
      setShake((n) => n + 1);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Ambient />
      <main className="codepad">
        <form
          key={shake}
          className={`glass codepad-inner ${error ? 'shake' : ''}`}
          onSubmit={(e) => void submit(e)}
        >
          <span className="hero-glyph" style={{ margin: '0 auto var(--space-4)' }}>
            <Icon name="key" size={22} />
          </span>
          <h1>Woodlands</h1>
          <p>Enter the access code you were given.</p>

          <input
            className="code-input"
            value={code}
            onChange={(e) => setCode(format(e.target.value))}
            placeholder="ABCD-1234"
            inputMode="text"
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck={false}
            autoComplete="one-time-code"
            aria-label="Access code"
            enterKeyHint="go"
          />

          {error && (
            <p className="code-error" role="alert">
              {error}
            </p>
          )}

          <button
            type="submit"
            className="btn btn-primary btn-block"
            style={{ marginTop: 'var(--space-4)' }}
            disabled={busy || code.replace(/-/g, '').length < 4}
          >
            {busy ? 'Checking…' : 'Enter'}
          </button>

          <p style={{ marginTop: 'var(--space-5)', fontSize: 12.5, marginBottom: 0 }}>
            Codes are time-limited and only open the parts of the house you were given.
          </p>
        </form>
      </main>
    </>
  );
}
